import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, finalize, switchMap, takeUntil } from 'rxjs/operators';
import {
  BackendError,
  Breakpoint,
  BreakpointState,
  HrefNavigationEvent,
  MediaQueryService,
  ValueValidator,
  AutoSuggestSymbol,
  BuySellService,
  IfFeatureService,
  SecuritySymbol,
  DataAnalyticsTaggingConstants,
  DataAnalyticsTaggingService
} from '@tdwb/core';

import { SiteWideSearchRestService } from './site-wide-search-rest.service';
import { RecentSearchItem, SearchResult, SearchResultType } from './site-wide-search.domain';

/**
 * Site Wide Search Component
 *
 * This is the component that creates a site wide search on the page
 */
@Component({
  selector: 'td-wb-site-wide-search',
  templateUrl: 'site-wide-search.component.html',
  styleUrls: ['site-wide-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SiteWideSearchComponent implements OnInit, OnDestroy {
  private static readonly DEBOUNCE_TIME: number = 500;

  searchInput: string;
  searchResult: SearchResult;
  isLoading: boolean;
  previousButtonClickInput: string;

  smallOrMediumScreen: boolean = false;
  smallScreen: boolean = false;
  stickyState: boolean = false;
  recentSearchResults: Array<RecentSearchItem> = [];
  previousSearchTerm: string;
  hasFocus: boolean = false;
  recentSearchSymbolsEnabled: boolean = false;
  recentSearchResultsUpToDate: boolean = false;
  @ViewChild('searchInputField') searchInputField: ElementRef;
  readonly allowableCharactersRegex: RegExp;
  readonly leadingSpecialCharactersRegex: RegExp;
  private mediaQueryServiceSubscription: Subscription;
  private readonly destroy: Subject<void> = new Subject<void>();
  private readonly searchInputValueChange: Subject<string> = new Subject<string>();
  private lastExpandedRow: number;

  constructor(private changeDetectorRef: ChangeDetectorRef,
              private siteWideSearchRestService: SiteWideSearchRestService,
              private mediaQueryService: MediaQueryService,
              private featureService: IfFeatureService,
              private buySellService: BuySellService,
              private dataAnalyticsTaggingService: DataAnalyticsTaggingService) {
    this.setupSearchInputValueChangeObserver();

    // For new universal search we are allowing an extra '.' and also do recent searches call
    if (this.getUniversalSearchFeatureFlag()) {
      this.allowableCharactersRegex = new RegExp('^[a-zA-Z0-9&$?,.’ \'\u00C0-\u00FF]*$');
      this.leadingSpecialCharactersRegex = new RegExp('^[&$?,.’ \'][a-zA-Z0-9&$?,.’ \'\u00C0-\u00FF]*$');
      this.recentSearchSymbolsEnabled = this.featureService.getFeatureFlag<boolean>('universalSearchRecentSearchSymbols');
      this.getRecentSearches();
    } else {
      this.allowableCharactersRegex = new RegExp('^[a-zA-Z0-9&$?,’ \'\u00C0-\u00FF]*$');
      this.leadingSpecialCharactersRegex = new RegExp('^[&$?,’ \'][a-zA-Z0-9&$?,’ \'\u00C0-\u00FF]*$');
    }
  }

  ngOnInit(): void {
    this.mediaQueryServiceSubscription = this.mediaQueryService.observe(Breakpoint.SM, Breakpoint.MD)
      .subscribe((breakpointState: BreakpointState): void => {
        this.smallOrMediumScreen = breakpointState.matches;
        this.smallScreen = breakpointState.isBreakpointMatch(Breakpoint.SM);
        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnDestroy(): void {
    if (this.mediaQueryServiceSubscription !== undefined) {
      this.mediaQueryServiceSubscription.unsubscribe();
    }
    this.destroy.next();
    this.destroy.complete();
  }

  setSearchQuery(symbolQuery: string): void {
    if (symbolQuery !== undefined && !this.hasError(symbolQuery)) {
      if (symbolQuery !== this.previousSearchTerm) {
        this.isLoading = true;
        this.searchInputValueChange.next(symbolQuery);
        this.previousSearchTerm = symbolQuery;
      }
    } else {
      this.searchResult = undefined;
    }
  }

  inputChange(inputValue: string): void {
    this.previousButtonClickInput = undefined; // reset button click check when user types
    this.lastExpandedRow = undefined;
    if (inputValue === undefined || inputValue === '') {
      this.dataAnalyticsTaggingService
        .emitTaggingEvent(DataAnalyticsTaggingConstants.getLinkClickEventData('td:wb:search:clear:result'));

      this.searchResult = undefined;
      this.previousSearchTerm = undefined;
      if (this.getUniversalSearchFeatureFlag()) {
        this.getRecentSearches();
      }
    } else {
      this.setSearchQuery(inputValue);
    }
  }

  onSearchButtonClicked(): void {
    if (this.searchInput === undefined || this.searchInput === '') {
      this.searchResult = undefined;
    } else if (this.previousButtonClickInput !== this.searchInput) {
      if (this.getUniversalSearchFeatureFlag() && !this.isInputValueInvalid(this.searchInput)) {
        this.setRecentSearches(this.searchInput);
      }
      // above check is to ensure we're not sending repeated requests with the same entered search input
      // if users click multiple times on search button
      this.setSearchQuery(this.searchInput);
      this.previousButtonClickInput = this.searchInput;
    }
  }

  displayResults(): boolean {
    return this.isNotEmptyInputValue();
  }

  displayTextSnippet(i: number): boolean {
    return !(this.smallOrMediumScreen && i >= 1);
  }

  displayHelpResults(): boolean {
    return this.searchResult !== undefined && this.searchResult.help !== undefined && this.searchResult.help.length > 0
      && !this.hasError(this.searchInput);
  }

  displaySymbolResults(): boolean {
    return this.searchResult !== undefined && this.searchResult.symbol !== undefined && this.searchResult.symbol.length > 0
      && !this.hasError(this.searchInput);
  }

  displayEducationResults(): boolean {
    return this.searchResult !== undefined && this.searchResult.education !== undefined && this.searchResult.education.length > 0
      && !this.hasError(this.searchInput);
  }

  displayNoResultsFound(): boolean {
    return !this.displayHelpResults() && !this.displayEducationResults() && !this.displaySymbolResults() || this.hasError(this.searchInput);
  }

  stickyStateChange(stickyState: boolean): void {
    this.stickyState = stickyState;
  }

  searchValueExists(): void {
    if (this.isInputValueInvalid(this.searchInputField.nativeElement.value)) {
      return;
    } else {
      this.setRecentSearch();
    }
  }

  setRecentSearch(value?: string): false {
    const index = this.recentSearchResults.find((result: RecentSearchItem)=>{
      return result.searchText === value;
    })?.priority;
    if (index !== undefined) {
      this.dataAnalyticsTaggingService.emitTaggingEvent(
        DataAnalyticsTaggingConstants.getLinkClickEventData(`td:wb:search:recent:search:${index - 1}`));
    }
    if (value) {
      this.searchInput = value;
    } else {
      this.searchInput = this.searchInputField.nativeElement.value;
    }
    this.setRecentSearches(this.searchInput);
    this.inputChange(this.searchInput);
    return false;
  }

  clearRecentSearches(recentSearchItem?: RecentSearchItem): void {
    const allRecentSearches = this.recentSearchResults.map((obj: RecentSearchItem) => ({
      searchText: obj.searchText,
      market: obj.market,
      symbolType: obj.symbolType
    }));
    if (recentSearchItem === undefined) {
      this.dataAnalyticsTaggingService.emitTaggingEvent(DataAnalyticsTaggingConstants.getLinkClickEventData('td:wb:search:clear:recent:searches'));
      this.siteWideSearchRestService.deleteRecentSearch(allRecentSearches)
        .pipe(takeUntil(this.destroy),
          finalize(() => this.changeDetectorRef.markForCheck())).subscribe();
      this.recentSearchResults = [];
      this.hasFocus = true;
    } else {
      this.siteWideSearchRestService.deleteRecentSearch([{
        searchText: recentSearchItem.searchText,
        market: recentSearchItem.market,
        symbolType: recentSearchItem.symbolType
      }])
        .pipe(takeUntil(this.destroy),
          finalize(() => this.changeDetectorRef.markForCheck())).subscribe();
      const index = allRecentSearches.findIndex((item: RecentSearchItem): boolean =>
        item.searchText === recentSearchItem.searchText && item.market === recentSearchItem.market);
      if (index !== -1) {
        this.dataAnalyticsTaggingService.emitTaggingEvent(DataAnalyticsTaggingConstants.getLinkClickEventData(`td:wb:search:clear:result:${index}`));
        if (index === this.lastExpandedRow) {
          this.lastExpandedRow = undefined;
        } else if (index < this.lastExpandedRow) {
          this.lastExpandedRow = this.lastExpandedRow - 1;
        }
        this.recentSearchResults.splice(index, 1);
      }
    }
    if (this.recentSearchResults.length === 0) {
      this.hasFocus = true;
    }
  }

  isHelpDisplayedFirst(): boolean {
    return this.searchResult !== undefined && this.searchResult.categoryOrder !== undefined &&
      this.searchResult.categoryOrder instanceof Array && this.searchResult.categoryOrder.length > 0 &&
      this.searchResult.categoryOrder[0] === SearchResultType.HELP;
  }

  hasError(searchInput: string): boolean {
    return searchInput !== undefined && searchInput !== '' &&
      (searchInput?.match(this.allowableCharactersRegex) === null ||
        searchInput?.match(this.leadingSpecialCharactersRegex) !== null);
  }

  createSearchValidator(): ValueValidator {
    return (): ValidationErrors | null => this.hasLeadingSpecialCharacters();
  }

  isRowExpanded(index: number): boolean {
    return index === this.lastExpandedRow;
  }

  toggleExpandRow(index: number): false {
    this.dataAnalyticsTaggingService.emitTaggingEvent(
      DataAnalyticsTaggingConstants.getLinkClickEventData(`td:wb:search:stock:${index}`));
    if (index === this.lastExpandedRow) {
      this.lastExpandedRow = undefined;
    } else {
      this.lastExpandedRow = index;
      if (this.recentSearchSymbolsEnabled) {
        const symbolSearchItem = this.searchResult?.symbol[index];
        if (symbolSearchItem !== undefined) {
          this.setRecentSymbolSelection({
            searchText: symbolSearchItem.symbol,
            market: symbolSearchItem.marketType,
            symbolType: symbolSearchItem.symbolType
          });
        }
      } else {
        this.setRecentSearches(this.searchInputField.nativeElement.value);
      }
    }
    return false;
  }

  toggleExpandRecentSearchRow(index: number): false {
    this.dataAnalyticsTaggingService.emitTaggingEvent(
      DataAnalyticsTaggingConstants.getLinkClickEventData(`td:wb:search:recent:search:${index}`));
    if (index === this.lastExpandedRow) {
      this.lastExpandedRow = undefined;
    } else {
      this.lastExpandedRow = index;
    }
    return false;
  }

  getSymbol(symbol: AutoSuggestSymbol): Pick<SecuritySymbol, 'tickerSymbol' | 'type' | 'market'> {
    return {
      tickerSymbol: symbol.symbol,
      type: symbol.symbolType.toString(),
      market: symbol.marketType
    } as Pick<SecuritySymbol, 'tickerSymbol' | 'type' | 'market'>;
  }

  getSymbolFromRecentSearch(symbol: RecentSearchItem): Pick<SecuritySymbol, 'tickerSymbol' | 'type' | 'market'> {
    return {
      tickerSymbol: symbol.searchText,
      type: symbol.symbolType.toString(),
      market: symbol.market
    } as Pick<SecuritySymbol, 'tickerSymbol' | 'type' | 'market'>;
  }

  setRecentSearchesAndNavigate(ref: HrefNavigationEvent, index: number): void {
    const searchSection = ref.ref.includes('help') ? 'helpful:articles' : 'video:lessons';
    this.dataAnalyticsTaggingService.emitTaggingEvent(
      DataAnalyticsTaggingConstants.getLinkClickEventData(`td:wb:${searchSection}:${index}`));
    this.setRecentSearches(this.searchInputField.nativeElement.value);
    if (ref !== undefined) {
      ref.navigate();
    }
  }

  isBuySellDisabled(symbol: AutoSuggestSymbol): boolean {
    return !this.buySellService.hasPermission(symbol.symbolType);
  }

  showRecentSearchSymbol(recentSearchItem: RecentSearchItem): boolean {
    return this.recentSearchSymbolsEnabled && recentSearchItem.market !== undefined;
  }

  private hasLeadingSpecialCharacters(): ValidationErrors | null {
    if (this.searchInput !== '' && this.searchInput?.match(this.leadingSpecialCharactersRegex) !== null) {
      return { special: true };
    }
    return null;
  }

  private setupSearchInputValueChangeObserver(): void {
    this.searchInputValueChange
      .pipe(
        debounceTime(SiteWideSearchComponent.DEBOUNCE_TIME),
        switchMap((searchQuery: string): Observable<SearchResult> => this.doSearch(searchQuery)),
        takeUntil(this.destroy)
      )
      .subscribe({
        next: (result: SearchResult): void => {
          this.searchResult = result;
          this.lastExpandedRow = undefined;
          this.isLoading = false;
          this.dataAnalyticsTaggingService.emitTaggingEvent(DataAnalyticsTaggingConstants.getSearchEventData(this.searchInput, this.getSearchResultCount(result).toString()));
          this.changeDetectorRef.markForCheck();
        },
        error: (error: BackendError): void => {
          // reset button click check on error in case user wants to press button to try again
          this.previousButtonClickInput = undefined;
          this.changeDetectorRef.markForCheck();
          this.setupSearchInputValueChangeObserver();
          throw error;
        }
      });
  }

  private getSearchResultCount(result: SearchResult): number {
    return (result.help?.length ?? 0 ) +
      (result.education?.length ?? 0)  +
      (result.symbol?.length ?? 0);
  }

  private getRecentSearches(): void {
    if (!this.recentSearchResultsUpToDate) {
      // Set here to ensure multiple calls are not placed at the same time to update
      this.recentSearchResultsUpToDate = true;
      this.siteWideSearchRestService.getRecentSearches()
        .pipe(takeUntil(this.destroy),
          finalize(() => this.changeDetectorRef.markForCheck()))
        .subscribe({
          next: (result: Array<RecentSearchItem>): void => {
            if (result.length > 0) {
              this.recentSearchResults = result;
            }
          },
          error: (errorResponse: Array<BackendError>): void => {
            if (errorResponse && errorResponse !== undefined) {
              this.recentSearchResults = [];
              this.recentSearchResultsUpToDate = false;
            }
          }
        });
    }
  }

  private setRecentSearches(searchText: string): void {
    this.siteWideSearchRestService.setRecentSearch(searchText)
      .pipe(takeUntil(this.destroy),
        finalize(() => this.changeDetectorRef.markForCheck()))
      .subscribe();
    this.recentSearchResultsUpToDate = false;
  }

  private setRecentSymbolSelection(symbolSearchItem: RecentSearchItem): void {
    this.siteWideSearchRestService.setRecentSymbolSearch(symbolSearchItem)
      .pipe(takeUntil(this.destroy),
        finalize(() => this.changeDetectorRef.markForCheck()))
      .subscribe();
    this.recentSearchResultsUpToDate = false;
  }

  private doSearch(searchQuery: string): Observable<SearchResult> {
    return this.siteWideSearchRestService.search(searchQuery);
  }

  private getUniversalSearchFeatureFlag(): boolean {
    return this.featureService.getFeatureFlag<boolean>('universalSearch');
  }

  private isNotEmptyInputValue(): boolean {
    return this.searchInput !== null && this.searchInput !== undefined && this.searchInput !== '';
  }

  private isInputValueInvalid(inputString: string): boolean {
    return inputString === undefined ||
      inputString === '' ||
      inputString?.match(this.allowableCharactersRegex) === null ||
      inputString?.match(this.leadingSpecialCharactersRegex) !== null;
  }
}
